import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * tailwind-merge no conoce las escalas propias del design system, y eso
 * rompía la promesa de que un consumidor puede sobrescribir el estilo de
 * un componente. Comprobado: `twMerge("rounded-ds-pill rounded-none")`
 * devolvía LAS DOS clases, porque `ds-pill` no es un valor que su
 * configuración por defecto reconozca como radio. La última definida en
 * el CSS ganaba, que no es necesariamente la que pidió quien llama.
 *
 * Aquí se le enseñan las escalas de `tailwind.config.cjs`. Los colores
 * (`ds-ink`, `ds-violet-pop`…) no hacen falta: esos sí los agrupa bien
 * por el prefijo (`text-`, `bg-`, `border-`).
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: [
        {
          rounded: [
            "ds-sm",
            "ds-md",
            "ds-lg",
            "ds-xl",
            "ds-2xl",
            "ds-3xl",
            "ds-4xl",
            "ds-pill",
            "ds-full",
          ],
        },
      ],
      shadow: [
        {
          shadow: [
            "ds-xs",
            "ds-card",
            "ds-card-hv",
            "ds-float",
            "ds-insight",
            "ds-cta",
            "ds-glow",
            "glass",
            "glass-lg",
            "glass-sm",
            "glass-purple",
          ],
        },
      ],
      "font-size": [
        {
          text: [
            "ds-display",
            "ds-h1",
            "ds-h2",
            "ds-h3",
            "ds-metric-xl",
            "ds-metric-md",
            "ds-body",
            "ds-body-lg",
            "ds-label",
            "ds-caption",
          ],
        },
      ],
      tracking: [{ tracking: ["ds-label", "ds-label-wide"] }],
    },
  },
});

/**
 * Une clases condicionales y resuelve conflictos de Tailwind: la última
 * utility gana (`cn("p-2", "p-4")` → `"p-4"`), que es lo que permite a un
 * consumidor sobrescribir el estilo de un componente vía `className`.
 *
 * En el sistema viejo había DOS versiones distintas de `cn` (una en
 * `src/lib/cn.ts` y otra en `src/lib/utils.ts`), ambas un simple
 * `filter(Boolean).join(" ")` sin resolución de conflictos. Aquí queda una
 * sola, y sí resuelve.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
